import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Row, Col } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as docx from 'docx';
import './App.css';

const emptyDataset = () => ({
  source_org: '',
  year: '',
  name: '',
  uri: '',
  access_date: '',
  note: '',
  access_type: '',
  license: '',
  license_uri: '',
  citation: '',
  citation_manual: false,
});


export default function ReadmeGenerator() {
  const [paperName, setPaperName] = useState(localStorage.getItem('paperName') || '');
  const [overview, setOverview] = useState(localStorage.getItem('overview') || '');

  const [dataSources, setDataSources] = useState(
    JSON.parse(localStorage.getItem('dataSources')) || [emptyDataset()]
  );

  const [instructions, setInstructions] = useState(localStorage.getItem('instructions') || '');
  const [exhibits, setExhibits] = useState(localStorage.getItem('exhibits') || '');
  const [computationalRequirements, setComputationalRequirements] = useState(
    localStorage.getItem('computationalRequirements') || ''
  );
  const [softwareRequirements, setSoftwareRequirements] = useState(
    localStorage.getItem('softwareRequirements') || ''
  );
  const [memoryRequirements, setMemoryRequirements] = useState(
    localStorage.getItem('memoryRequirements') || ''
  );
  const [codeDescription, setCodeDescription] = useState(
    localStorage.getItem('codeDescription') || ''
  );
  const [folderStructure, setFolderStructure] = useState(
    localStorage.getItem('folderStructure') || ''
  );

  const [rightsAccess, setRightsAccess] = useState(JSON.parse(localStorage.getItem('rightsAccess')) || false);
  const [rightsRedistribute, setRightsRedistribute] = useState(
    JSON.parse(localStorage.getItem('rightsRedistribute')) || false
  );

  useEffect(() => {
    localStorage.setItem('paperName', paperName);
    localStorage.setItem('overview', overview);
    localStorage.setItem('dataSources', JSON.stringify(dataSources));
    localStorage.setItem('instructions', instructions);
    localStorage.setItem('exhibits', exhibits);
    localStorage.setItem('computationalRequirements', computationalRequirements);
    localStorage.setItem('softwareRequirements', softwareRequirements);
    localStorage.setItem('memoryRequirements', memoryRequirements);
    localStorage.setItem('codeDescription', codeDescription);
    localStorage.setItem('folderStructure', folderStructure);
    localStorage.setItem('rightsAccess', JSON.stringify(rightsAccess));
    localStorage.setItem('rightsRedistribute', JSON.stringify(rightsRedistribute));
  }, [
    paperName,
    overview,
    dataSources,
    instructions,
    exhibits,
    computationalRequirements,
    softwareRequirements,
    memoryRequirements,
    codeDescription,
    folderStructure,
    rightsAccess,
    rightsRedistribute,
  ]);

  const clearAll = () => {
    localStorage.clear();
    setPaperName('');
    setOverview('');
    setDataSources([emptyDataset()]);
    setInstructions('');
    setExhibits('');
    setComputationalRequirements('');
    setSoftwareRequirements('');
    setMemoryRequirements('');
    setCodeDescription('');
    setFolderStructure('');
    setRightsAccess(false);
    setRightsRedistribute(false);
  };

  const addDataSource = () => setDataSources([...dataSources, emptyDataset()]);

  const generateCitation = (d) => {
    const source = (d.source_org || '').trim();
    const year = (d.year || '').trim();
    const name = (d.name || '').trim();
    const uri = (d.uri || '').trim();
    const accessDate = (d.access_date || '').trim();

    if (!source || !year || !name) return '';

    let citation = `${source}. ${year}. "${name}" [dataset].`;

    if (uri) {
      citation += ` ${uri}.`;
    }

    if (accessDate) {
      citation += ` Accessed ${accessDate}.`;
    }

    return citation;
  };


  const updateDataSource = (index, key, value) => {
    const next = [...dataSources];
    next[index][key] = value;

    // If user edits citation directly, mark as manual
    if (key === 'citation') {
      next[index].citation_manual = true;
      setDataSources(next);
      return;
    }

    // Auto-generate citation only if not manually overridden
    if (!next[index].citation_manual) {
      next[index].citation = generateCitation(next[index]);
    }

    setDataSources(next);
  };


  const removeDataSource = (index) => {
    const next = [...dataSources];
    next.splice(index, 1);
    setDataSources(next.length ? next : [emptyDataset()]);
  };

  const generateDocx = () => {

    const addKeyValue = (label, value) => {
      docContent.push(new docx.Paragraph({ text: `${label}: ${value && value.toString().trim() ? value : '-'}` }));
    };

    const rightsText = [
      rightsAccess
        ? 'I certify that the author(s) of the manuscript have legitimate access to and permission to use the data used in this manuscript.'
        : '',
      rightsRedistribute
        ? 'I certify that the author(s) of the manuscript have documented permission to redistribute/publish the data contained within this replication package. Appropriate permissions are documented in the LICENSE.txt file.'
        : '',
    ]
      .filter(Boolean)
      .join('\n');

    const docContent = [];

    const addParagraph = (title, text) => {
      if (text && text.trim()) {
        docContent.push(new docx.Paragraph({ text: title, heading: docx.HeadingLevel.HEADING_2 }));
        text.split('\n').forEach((line) => {
          if (line.trim()) docContent.push(new docx.Paragraph({ text: line }));
        });
        docContent.push(new docx.Paragraph({ text: '' }));
      }
    };

    if (paperName.trim()) {
      docContent.push(
        new docx.Paragraph({
          text: `README for the Reproducibility Package for ${paperName}`,
          heading: docx.HeadingLevel.HEADING_1,
        })
      );
      docContent.push(new docx.Paragraph({ text: '' }));
    }

    if (overview.trim()) addParagraph('Overview', overview);

    if (dataSources && dataSources.length > 0) {
      docContent.push(
        new docx.Paragraph({ text: 'Data Availability Statement', heading: docx.HeadingLevel.HEADING_2 })
      );

      dataSources
        .filter((d) => Object.values(d).some((v) => (v || '').toString().trim()))
        .forEach((d, i) => {
          docContent.push(
            new docx.Paragraph({
              text: `${i + 1}. ${d.name && d.name.trim() ? d.name : 'Dataset'}`,
              heading: docx.HeadingLevel.HEADING_3,
            })
          );

          addKeyValue('Source', d.source_org);
          addKeyValue('Year', d.year);
          addKeyValue('URL', d.uri);
          addKeyValue('Access Date (Month-Year)', d.access_date);
          addKeyValue('Note', d.note);
          addKeyValue('Access Type', d.access_type);
          addKeyValue('License', d.license);
          addKeyValue('License URL', d.license_uri);

          const cit = (d.citation && d.citation.trim()) ? d.citation : (generateCitation(d) || '');
          addKeyValue('Citation', cit);

          docContent.push(new docx.Paragraph({ text: '' }));
        });

      docContent.push(new docx.Paragraph({ text: '' }));
    }

    if (rightsText.trim()) addParagraph('Statement about Rights', rightsText);
    if (instructions.trim()) addParagraph('Instructions for Replicators', instructions);
    if (computationalRequirements.trim())
      addParagraph('Computational Requirements and Runtime', computationalRequirements);
    if (softwareRequirements.trim()) addParagraph('Software Requirements', softwareRequirements);
    if (memoryRequirements.trim()) addParagraph('Memory and Storage Requirements', memoryRequirements);
    if (codeDescription.trim()) addParagraph('Code Description', codeDescription);
    if (exhibits.trim()) addParagraph('List of Exhibits', exhibits);
    if (folderStructure.trim()) addParagraph('Folder Structure', folderStructure);

    const doc = new docx.Document({
      sections: [
        {
          properties: {},
          children: docContent,
        },
      ],
    });

    docx.Packer.toBlob(doc).then((blob) => {
      const safeName = (paperName || 'README').replace(/[^\w\s-]/g, '').trim() || 'README';
      saveAs(blob, `${safeName}_README.docx`);
    });
  };

  return (
    <Container fluid className="p-4 w-100">
      <Row className="mb-3 w-100">
        <Col xs={12} className="d-flex justify-content-between">
          <h2>README Generator</h2>
          <Button variant="danger" onClick={clearAll}>
            Clear All
          </Button>
        </Col>
      </Row>

<Card className="mb-4 border-0 bg-light">
  <Card.Body className="small">
    <strong>Important:</strong> Please use this README template to document your replication package.
    Completing this form carefully and in full will significantly reduce review time and facilitate faster
    reproducibility verification. It also serves as a practical self-review tool to ensure your package meets
    reproducibility standards before submission.
    <br /><br />

    This template draws on best practices developed by the Social Science Data Editors (SSDE).
    Additional standards and examples are available at:
    <br />
    <a
      href="https://social-science-data-editors.github.io/template_README/template-README.html"
      target="_blank"
      rel="noopener noreferrer"
    >
      Social Science Data Editors – Template README
    </a>
    <br /><br />

    For World Bank–specific reproducibility guidance and policies, visit:
    <br />
    <a
      href="https://worldbank.github.io/wb-reproducible-research-repository/"
      target="_blank"
      rel="noopener noreferrer"
    >
      worldbank.github.io/wb-reproducible-research-repository
    </a>
    <br /><br />

    If you have questions, please contact{' '}
    <a href="mailto:reproducibility@worldbank.org">
      reproducibility@worldbank.org
    </a>.
  </Card.Body>
</Card>


      <Row>
        <Card className="p-3 mb-4">
          <Form>
            <Form.Group style={{ marginBottom: '3rem' }}>
              <Form.Label>
                <h3>Paper Title</h3>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Paper Name"
                value={paperName}
                onChange={(e) => setPaperName(e.target.value)}
              />
            </Form.Group>
            <Form.Group style={{ marginBottom: '3rem' }}>
              <Form.Label>
                <h3>Overview</h3>
              </Form.Label>

              <Form.Text className="text-muted-small d-block mb-3">
                Provide a concise overview of the contents of this reproducibility package.
                Briefly describe the data sources used, software required, main scripts,
                outputs generated (tables/figures), and expected runtime.
                This section should guide a replicator from start to finish.
                <br /><br />

                <b>Example:</b><br />
                <i>
                  The code in this replication package constructs the analysis file from three
                  data sources using Stata and Julia. Two main scripts execute the full workflow
                  and generate the data for 15 figures and 3 tables in the manuscript.
                  The replicator should expect the code to run for approximately 14 hours.
                </i>
                <br /><br />
              </Form.Text>

              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Overview"
                value={overview}
                onChange={(e) => setOverview(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <h3>Data Availability Statement</h3>
              </Form.Label>

              <Form.Text className="text-muted-small d-block mb-3">
                A reproducibility package must begin from clearly documented <b>source data</b>.
                Source data should be citable, properly described, and either publicly accessible,
                archived in a World Bank–approved repository, or accompanied by clear access procedures if restricted.
                These data sources constitute the official starting point for reproduction.
                <br /><br />

                Below, please provide the documentation for each <b>data source</b> used in this package.
                <b> A data source is not necessarily the same as an input file.</b> For example, a survey may be split across
                multiple files (modules, rounds, formats), but you typically only need to document the survey <b>once</b> and
                list the relevant file(s) and locations in the <b>Note</b> field.
                <br /><br />

                For each data source, provide the publishing <b>Source</b>, <b>Year</b>, <b>Data name</b>, <b>URL</b>,
                and <b>Access Date (Month–Year)</b>. Use the <b>Note</b> field to indicate file locations within the replication
                package and any relevant access instructions (e.g., login required, request form, indicator accessed).
                <br /><br />

                The <b>Citation</b> field is automatically generated from the Source, Year, Data name, and URL.
                You may edit it manually if needed. If modified, it will no longer auto-update unless you click
                “Auto-fill citation.”
                <br /><br />

                Choose the access category that most accurately reflects the data’s redistribution and permission status from the dropdown
                below (descriptions are included in the dropdown menu).
                <br /><br />
              </Form.Text>


              <Form.Text className="text-muted-medium d-block mb-3">
                <b>Example Data Availability</b>
              </Form.Text>

              <div className="border rounded p-3 mb-3 small bg-light">
                <strong><span className="text-muted">Data name:</span> Youth Employment and Skills Development Project - Apprenticeship Youth Surveys (2014–2018)</strong><br />
                <span className="text-muted">Source:</span> World Bank, Development Data Group (2024)<br />
                <span className="text-muted">URL:</span> microdata.worldbank.org/catalog/6267<br />
                <span className="text-muted">Note:</span> File location: SurveyData2016/1_Raw/y1_raw_youth_baseline.dta; SurveyData2016/1_Raw/y2_raw_youth_midline.dta; SurveyData2018/1_Raw/y3_raw_youth_endline.dta.<br />
                <span className="text-muted">Access Date:</span> July 2024<br />
                <span className="text-muted">Access Type:</span> Accessible<br />
                <span className="text-muted">License:</span> Public Use License (World Bank Microdata Library)<br />
                <span className="text-muted">License URL:</span> hhttps://microdata.worldbank.org/terms-of-use#public_use<br />
                <span className="text-muted">Citation:</span> Crépon, B., & Premand, P. (2024). "Youth Employment and Skills Development Project - Apprenticeship Youth Surveys 2014–2018" [dataset]. https://doi.org/10.48529/766E-B621
              </div>

              <div className="border rounded p-3 mb-3 small bg-light">
                <strong><span className="text-muted">Data name:</span> World Development Indicators</strong><br />
                <span className="text-muted">Source:</span> World Bank (2025)<br />
                <span className="text-muted">URL:</span> databank.worldbank.org/source/world-development-indicators<br />
                <span className="text-muted">Note:</span> File location: data/raw/wdi.csv; Indicators used include GDP per capita, population, and labor force participation rate.<br />
                <span className="text-muted">Access Date:</span> September 2025<br />
                <span className="text-muted">Access Type:</span> Open<br />
                <span className="text-muted">License:</span> CC BY 4.0<br />
                <span className="text-muted">License URL:</span> https://www.worldbank.org/en/about/legal/terms-of-use-for-datasets<br />
                <span className="text-muted">Citation:</span> World Bank. 2025. "World Development Indicators" [dataset]. https://databank.worldbank.org/source/world-development-indicators
              </div>

              <div className="border rounded p-3 mb-3 small bg-light">
                <strong><span className="text-muted">Data name:</span> Annual Crop Data</strong><br />
                <span className="text-muted">Source:</span> World Bank (2025)<br />
                <span className="text-muted">Note:</span> Files location: data/crop/crop-cover.xlsx.<br />
                <span className="text-muted">Access Date:</span> January 2025<br />
                <span className="text-muted">Access Type:</span> Forthcoming data<br />
                <span className="text-muted">Citation:</span> World Bank (2025). "Annual Crop Data" [dataset]. Forthcoming in the World Bank Development Data Hub.
              </div>

              <div className="border rounded p-3 small bg-light">
                <strong><span className="text-muted">Data name:</span> Relação Anual de Informações Sociais (RAIS)</strong><br />
                <span className="text-muted">Source:</span> Brazil Ministry of Labor (2021)<br />
                <span className="text-muted">Note:</span> File location: data/raw/Estab_year.dta (1994–2020).
                The dataset contains employer and employee identifiers, municipality and industry codes, wages,
                and demographic information. Access is restricted and was obtained through a formal agreement
                between the Ministry of Labor and the World Bank; redistribution is not permitted.<br />
                <span className="text-muted">Access Date:</span> December 2021<br />
                <span className="text-muted">Access Type:</span> Restricted data<br />
                <span className="text-muted">Citation:</span> Brazil Ministry of Labor. 2021. "Relação Anual de Informações Sociais (RAIS)" (1994–2020) [dataset]. Unpublished data. Accessed February 29, 2022.
              </div>

              {dataSources.map((item, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <Row className="align-items-center mb-3">
                      <Col>
                        <h5 className="mb-0">
                          Dataset {index + 1}{item.name ? `: ${item.name}` : ''}
                        </h5>
                      </Col>
                      <Col xs="auto">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeDataSource(index)}
                          aria-label={`Remove dataset ${index + 1}`}
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>

                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group controlId={`ds-source-${index}`}>
                          <Form.Label>Source</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., World Bank, ILO, OECD"
                            value={item.source_org}
                            onChange={(e) => updateDataSource(index, 'source_org', e.target.value)}
                            aria-describedby={`ds-source-help-${index}`}
                          />
                          <Form.Text id={`ds-source-help-${index}`} className="text-muted">
                            Organization responsible for publishing the dataset.
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={2}>
                        <Form.Group controlId={`ds-year-${index}`}>
                          <Form.Label>Year</Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            placeholder="YYYY"
                            value={item.year}
                            onChange={(e) => updateDataSource(index, 'year', e.target.value)}
                          />
                          <Form.Text id={`ds-year-help-${index}`} className="text-muted">
                            Year the dataset was published or version of the dataset (use "n.d" if not available).
                          </Form.Text>

                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId={`ds-name-${index}`}>
                          <Form.Label>Data name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., World Development Indicators; LSMS-ISA (Country-Year); RAIS (1994–2020)"
                            value={item.name}
                            onChange={(e) => updateDataSource(index, 'name', e.target.value)}
                          />
                          <Form.Text id={`ds-name-help-${index}`} className="text-muted">
                            Official name of the data. (e.g., World Development Indicators; Household Survey (Country-Year); Annual Crop Data)
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={8}>
                        <Form.Group controlId={`ds-uri-${index}`}>
                          <Form.Label>URL</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="URL"
                            value={item.uri}
                            onChange={(e) => updateDataSource(index, 'uri', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group controlId={`ds-accessdate-${index}`}>
                          <Form.Label>Access Date (Month-Year)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., September 2025"
                            value={item.access_date}
                            onChange={(e) => updateDataSource(index, 'access_date', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group controlId={`ds-note-${index}`}>
                          <Form.Label>Note</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="File location, access instructions, indicators used, etc."
                            value={item.note}
                            onChange={(e) => updateDataSource(index, 'note', e.target.value)}
                          />
                          <Form.Text id={`ds-note-help-${index}`} className="text-muted">
                            Include the file location in the folder structure (e.g., data/raw/dataset.dta), any specific access instructions, and/or key indicators used from the dataset.
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group controlId={`ds-access-${index}`}>
                          <Form.Label>Access Type</Form.Label>
                          <Form.Select
                            value={item.access_type}
                            onChange={(e) => updateDataSource(index, 'access_type', e.target.value)}
                          >
                            <option value="">Select</option>

                            <option value="Open">
                              Open — Publicly available and redistributable
                            </option>

                            <option value="Accessible">
                              Accessible — Publicly downloadable, but redistribution not permitted
                            </option>

                            <option value="Limited-access">
                              Limited-access — Requires purchase or human approval
                            </option>

                            <option value="Forthcoming">
                              Forthcoming — Expected in WB Microdata Library or Development Data Hub
                            </option>

                            <option value="Restricted">
                              Restricted — Access granted directly to authors under custom terms; redistribution not permitted
                            </option>

                            <option value="Unsure">
                              Unsure / do not know yet
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId={`ds-license-${index}`}>
                          <Form.Label>License</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="License"
                            value={item.license}
                            onChange={(e) => updateDataSource(index, 'license', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId={`ds-licenseurl-${index}`}>
                          <Form.Label>License URL</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="License URL"
                            value={item.license_uri}
                            onChange={(e) => updateDataSource(index, 'license_uri', e.target.value)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group controlId={`ds-citation-${index}`}>
                          <Form.Label>Citation</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder='Auto-generated: Source. Year. "Data Name" [dataset]. URL'
                            value={item.citation}
                            onChange={(e) => updateDataSource(index, 'citation', e.target.value)}
                          />
                          <Form.Text id={`ds-citation-help-${index}`} className="text-muted">
                            Auto-generated citation based on the other fields. You can edit this directly to override the auto-generated version if needed (for instance
                            the data source has a suggested citation).
                          </Form.Text>
                          <div className="mt-2 d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                const next = [...dataSources];
                                next[index].citation_manual = false;
                                next[index].citation = generateCitation(next[index]);
                                setDataSources(next);
                              }}
                              aria-label={`Auto-fill citation for dataset ${index + 1}`}
                            >
                              Auto-fill citation
                            </Button>
                            {item.citation_manual && (
                              <span className="text-muted small align-self-center">
                                Manual override enabled
                              </span>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}


              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '3rem' }}>
                <Button variant="secondary" onClick={addDataSource}>
                  + Add Data
                </Button>
              </div>

              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Statement about Rights</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">Please check all that apply.</Form.Text>
                <Form.Check
                  type="checkbox"
                  label="I certify that the author(s) of the manuscript have legitimate access to and permission to use the data used in this manuscript."
                  checked={rightsAccess}
                  onChange={(e) => setRightsAccess(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="I certify that the author(s) of the manuscript have documented permission to redistribute/publish the data contained within this replication package. Appropriate permissions are documented in the LICENSE.txt file."
                  checked={rightsRedistribute}
                  onChange={(e) => setRightsRedistribute(e.target.checked)}
                />
              </Form.Group>

              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Instructions for Replicators</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">
                  Provide clear, step-by-step instructions explaining how to run the replication package.
                  Indicate which files to execute, any required software or packages, and where outputs will be saved.
                  Instructions should be written in simple, human-readable terms and describe the process linearly,
                  assuming no prior knowledge of the project.
                  <br /><br />
                  <b>Example:</b><br />To produce the exhibits in the paper a new user should. <br />
                  1. Install the required Stata packages (e.g., <i>reghdfe</i>, <i>estout</i>).<br />
                  2. Run the script <i>main.do</i>, which executes the full analysis pipeline.<br />
                  3. The script will save all tables and figures in the <i>outputs</i> folder.
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Instructions for Replicators"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </Form.Group>



              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Software Requirements</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">
                  List all software requirements, including versions, dependencies, and libraries.
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Software Requirements"
                  value={softwareRequirements}
                  onChange={(e) => setSoftwareRequirements(e.target.value)}
                />
              </Form.Group>

              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>List of Exhibits</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">
                  Clearly identify and document tables and figures as they appear in the manuscript.
                  This section is needed when file names do not clearly correspond to exhibit numbers.
                  Note any tables or figures that include manual changes from the code outputs.
                  Please also indicate whether there are any tables that do not show analysis results.
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="List of Exhibits"
                  value={exhibits}
                  onChange={(e) => setExhibits(e.target.value)}
                />
              </Form.Group>

              {/* Technology Environment */}
              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Technology Environment (optional but recommended)</h3>
                </Form.Label>

                <Form.Text className="text-muted d-block mb-2">
                  Specify the operating system, processor, and available memory used to reproduce the exhibits. 
                  Example: OS: Windows 10; Processor: Intel Core i7-9700K; Memory: 16 GB RAM.
                </Form.Text>

                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={`• OS:
• Processor:
• Memory available:`}
                  value={computationalRequirements}
                  onChange={(e) => setComputationalRequirements(e.target.value)}
                />
              </Form.Group>


              {/* Runtime */}
              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Runtime (optional but recommended)</h3>
                </Form.Label>

                <Form.Text className="text-muted d-block mb-2">
                  Report the total time required to reproduce all tables and figures.
                </Form.Text>

                <Form.Control
                  type="text"
                  placeholder="e.g., 6 minutes; ~2 hours"
                  value={memoryRequirements}
                  onChange={(e) => setMemoryRequirements(e.target.value)}
                />
              </Form.Group>

              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Code Description (optional but recommended)</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">Provide an overview of the program files and their purpose.</Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Code Description"
                  value={codeDescription}
                  onChange={(e) => setCodeDescription(e.target.value)}
                />
              </Form.Group>



              <Form.Group style={{ marginBottom: '3rem' }}>
                <Form.Label>
                  <h3>Folder Structure (optional but recommended)</h3>
                </Form.Label>
                <Form.Text className="text-muted d-block mb-3">
                  Describe the folder structure and organization of replication files.
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Folder Structure"
                  value={folderStructure}
                  onChange={(e) => setFolderStructure(e.target.value)}
                />
              </Form.Group>
            </Form.Group>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="primary" onClick={generateDocx}>
                Generate Word Document
              </Button>
            </div>
          </Form>
        </Card>
      </Row>
    </Container >
  );
}

